import { noargs, safeGet } from './bits'
import { keys, unary, hasIn, toPath, partial, isArray, isObject, isObjectLike } from 'lodash-es'

import StrictObj from './strict-obj'
import debugFields from './debug-fields'

const alphabeticalRE = /^\d+$/
const alphabeticalKeys = (v) => keys(v).filter(isAlphabeticalKey)
const isAlphabeticalKey = (v) => !alphabeticalRE.test(v)
const hasAlphabeticalKeys = (v) => isObjectLike(v) && (!isArray(v) || alphabeticalKeys(v).length !== 0)

import { defineHiddenGetter } from './define-getter'

const MARKER = '__attachSettersMarker__'
const TOGGLE = 'toggle'
const SETTERS = 'setters'
const TERMINATE = 'terminate'
const BIND_TRUE = 'bindTrue'
const BIND_FALSE = 'bindFalse'

export default function attachSetters(rootObj, attachKey = SETTERS) {
  defineHiddenGetter({
    obj: rootObj,
    key: attachKey,
    get: make
  })

  return rootObj

  function make() {
    const setters = makeSetters(rootObj)
    setters[TOGGLE] = makeTogglers(rootObj)
    setters[BIND_TRUE] = makeBinders(true, rootObj)
    setters[BIND_FALSE] = makeBinders(false, rootObj)
    return setters
  }

  function makeSetters(currObj, rootObj = currObj, basePath = '') {
    const currKeys = alphabeticalKeys(currObj)
    const currSetters = StrictObj({})

    currKeys.forEach((key) => {
      const subObj = currObj[key]
      const subPath = basePath + `["${key}"]`
      if (hasAlphabeticalKeys(subObj)) {
        defineHiddenGetter({
          obj: currSetters,
          key,
          get: partial(makeSetters, subObj, rootObj, subPath)
        })
      } else {
        currSetters[key] = unary(partial(setAndReturn, rootObj, subPath))
        currSetters[key][TERMINATE] = currSetters[key]
        debugFields(currSetters[key], {
          _subPath: subPath,
          _rootObj: rootObj
        })
      }
    })

    currSetters[MARKER] = true
    currSetters[TERMINATE] = unary(partial(setAndReturn, rootObj, basePath))
    debugFields(currSetters, {
      _rootObj: rootObj,
      _basePath: basePath
    })

    return currSetters
  }

  function makeTogglers(currObj, rootObj = currObj, basePath = '') {
    const currKeys = alphabeticalKeys(currObj)
    const currTogglers = StrictObj({})

    currKeys.forEach((key) => {
      const subObj = currObj[key]
      const subPath = basePath + `["${key}"]`
      if (hasAlphabeticalKeys(subObj)) {
        defineHiddenGetter({
          obj: currTogglers,
          key,
          get: partial(makeTogglers, subObj, rootObj, subPath)
        })
      } else {
        currTogglers[key] = noargs(partial(toggleAndReturn, rootObj, subPath))
        currTogglers[key][TERMINATE] = currTogglers[key]
        debugFields(currTogglers[key], {
          _subPath: subPath,
          _rootObj: rootObj
        })
      }
    })

    currTogglers[MARKER] = true
    currTogglers[TERMINATE] = noargs(partial(toggleAndReturn, rootObj, basePath))
    debugFields(currTogglers, {
      _rootObj: rootObj,
      _basePath: basePath
    })

    return currTogglers
  }

  function makeBinders(boundVal, currObj, rootObj = currObj, basePath = '') {
    const currKeys = alphabeticalKeys(currObj)
    const currBinders = StrictObj({})

    currKeys.forEach((key) => {
      const subObj = currObj[key]
      const subPath = basePath + `["${key}"]`
      if (hasAlphabeticalKeys(subObj)) {
        defineHiddenGetter({
          obj: currBinders,
          key,
          get: partial(makeBinders, boundVal, subObj, rootObj, subPath)
        })
      } else {
        currBinders[key] = noargs(partial(setAndReturn, rootObj, subPath, boundVal))
        currBinders[key][TERMINATE] = currBinders[key]
        debugFields(currBinders[key], {
          _subPath: subPath,
          _rootObj: rootObj,
          _boundVal: boundVal
        })
      }
    })

    currBinders[MARKER] = true
    currBinders[TERMINATE] = noargs(partial(setAndReturn, rootObj, basePath, boundVal))
    debugFields(currBinders, {
      _rootObj: rootObj,
      _basePath: basePath,
      _boundVal: boundVal
    })

    return currBinders
  }
}

function setAndReturn(rootObj, subPath, val) {
  // lodash.set does not override equal values

  const path = toPath(subPath)
  const key = path.slice(-1)
  let currObj = rootObj

  for (let index = 0, length = path.length; index < length - 1; index++) {
    const segment = path[index]
    const isExist = hasIn(currObj, segment) && isObject(currObj[segment])
    currObj = isExist
      ? currObj[segment]
      : currObj[segment] = {}
  }

  currObj[key] = val
  return val
}

function toggleAndReturn(rootObj, subPath) {
  const actual = safeGet(rootObj, subPath, false)
  const toggled = !actual
  return setAndReturn(rootObj, subPath, toggled)
}
